import { ReactElement, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import BadgeAudible from '../badge-audible/BadgeAudible';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useTranslation } from 'react-i18next';
import styles from './InteractionHeader.module.scss';
import GeofenceInteractionTypeRenderer from '@customer/components/tables/cells/geofence-interaction-type/GeofenceInteractionTypeRenderer';
import VehicleInteractionTypeRenderer from '@customer/components/tables/cells/vehicle-interaction-type/VehicleInteractionTypeRenderer';
import { AssetInvestigationType, InvestigationModel } from '@customer/store/apis/investigationsApi';
import InvestigationNotification from '../investigation-notification/InvestigationNotification';
import { useAppDispatch } from '@customer/store/hooks';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { parseInvestigationType } from '@customer/shared/utils/investigationHelper';
import { Investigatable } from '@customer/store/apis/interactionsApi';

interface InteractionShape extends Investigatable, Pick<InvestigationModel, 'aud' | 'ts'> {}

interface InteractionHeaderProps {
  interactionType: AssetInvestigationType;
  interaction?: InteractionShape;
}

export const InteractionHeader = ({
  interactionType,
  interaction,
}: InteractionHeaderProps): ReactElement => {
  const { t } = useTranslation();
  const { showTimeAndDateFromIso } = useLocaleFormatter();

  const dispatch = useAppDispatch();

  const onInvestigationContinue = useCallback(() => {
    dispatch(closeSidePanel());
  }, [dispatch]);

  const { vi, gi } = parseInvestigationType(interactionType);

  return (
    <div className={styles.header}>
      <div className={styles.header__titleRow}>
        {interaction ? (
          vi ? (
            <VehicleInteractionTypeRenderer value={vi} size="large" />
          ) : gi ? (
            <GeofenceInteractionTypeRenderer value={gi} />
          ) : null
        ) : (
          <Skeleton width="200px" height="36px" />
        )}
        {interaction ? (
          typeof interaction.aud === 'boolean' ? (
            <BadgeAudible isAudible={interaction.aud} />
          ) : null
        ) : (
          <Skeleton width="130px" height="32px" />
        )}
      </div>

      <div className={styles.header__subtitleRow}>
        {interaction ? (
          <div className={styles.subtitle}>
            {vi ? t('VehicleInteraction') : gi ? t('GeofenceInteraction') : ''}
          </div>
        ) : (
          <Skeleton width="140px" height="24px" />
        )}
        {interaction ? (
          <div className={styles.timestamp}>{showTimeAndDateFromIso(interaction.ts)}</div>
        ) : (
          <Skeleton width="150px" />
        )}
      </div>
      {interaction?.invest && (
        <div className={styles.header__subtitleRow}>
          <InvestigationNotification
            investigationId={interaction.invest.id}
            status={interaction.invest.st}
            onContinue={onInvestigationContinue}
          />
        </div>
      )}
    </div>
  );
};

export default InteractionHeader;
